import { useAppPreferences } from "../../../lib/preferences";
import { LoadState, Section, StatusChip } from "../ui";
import { campaignStatusKey, deriveCampaignState } from "./campaignState";
import {
  budgetRemainingDisplay,
  displayCount,
  displayMoney,
  usesRemainingDisplay,
} from "./money";
import type { PromotionRecord } from "./types";
import "./promotions.css";

function discountSummary(record: PromotionRecord): string {
  const config = record.current_revision.configuration;
  const cap = config.max_discount_amount ? ` · ≤ ${displayMoney(config.max_discount_amount)}` : "";
  if (config.discount_type === "percent") {
    return `${config.discount_percent ?? "?"}%${cap}`;
  }
  return `${displayMoney(config.fixed_amount)}${cap}`;
}

/**
 * Read-only campaign list. Metrics come from props exactly as supplied —
 * reserved (quoted/hold) credit is shown separately from consumed (actual)
 * credit and from later refunded credit. Pause/resume are plain callbacks;
 * the container owns confirmation, reasons, and the server call.
 */
export function PromotionList({
  canEdit,
  error = null,
  loading = false,
  onCreate,
  onPause,
  onResume,
  onRetry,
  onSelect,
  promotions,
}: {
  canEdit: boolean;
  error?: string | null;
  loading?: boolean;
  onCreate?: () => void;
  onPause?: (record: PromotionRecord) => void;
  onResume?: (record: PromotionRecord) => void;
  onRetry?: () => void;
  onSelect?: (record: PromotionRecord) => void;
  promotions: PromotionRecord[];
}) {
  const { t } = useAppPreferences();
  const showActions = canEdit && (onPause !== undefined || onResume !== undefined);

  return (
    <Section
      actions={
        canEdit && onCreate ? (
          <button className="ops-button" data-size="sm" data-variant="primary" onClick={onCreate} type="button">
            {t("New campaign")}
          </button>
        ) : null
      }
      description={t("Budgets and use limits count across all revisions of a campaign.")}
      title={t("Booking campaigns")}
    >
      <LoadState
        empty={t("No campaigns yet.")}
        error={error}
        isEmpty={promotions.length === 0}
        loading={loading}
        onRetry={onRetry ?? (() => undefined)}
      >
        <div className="console-table-wrap">
          <table className="console-table">
            <thead>
              <tr>
                <th scope="col">{t("Campaign")}</th>
                <th scope="col">{t("Savings")}</th>
                <th scope="col">{t("State")}</th>
                <th scope="col">{t("Used")}</th>
                <th scope="col">{t("Budget left")}</th>
                {showActions ? <th scope="col">{t("Actions")}</th> : null}
              </tr>
            </thead>
            <tbody>
              {promotions.map((record) => {
                const state = deriveCampaignState(record);
                const config = record.current_revision.configuration;
                const usesLeft = usesRemainingDisplay(config.max_uses, record.metrics.remaining_uses);
                const uses =
                  usesLeft.kind === "unlimited"
                    ? t("{used} used", { used: record.metrics.consumed_uses })
                    : t("{used} used · {left} left", {
                        used: record.metrics.consumed_uses,
                        left: usesLeft.kind === "unknown" ? t("Unknown") : displayCount(usesLeft.value),
                      });
                const budgetLeft = budgetRemainingDisplay(config.budget_amount, record.metrics.remaining_budget_amount);
                return (
                  <tr key={record.id}>
                    <td>
                      {onSelect ? (
                        <button className="console-link promo-name-button" onClick={() => onSelect(record)} type="button">
                          {config.name_en}
                        </button>
                      ) : (
                        config.name_en
                      )}
                      <span className="console-muted block">
                        {record.code ?? t("Automatic")}
                        {config.first_booking_only ? ` · ${t("First booking only")}` : ""}
                      </span>
                    </td>
                    <td>{discountSummary(record)}</td>
                    <td>
                      <StatusChip status={campaignStatusKey(state)} />
                    </td>
                    <td>{uses}</td>
                    <td>
                      {budgetLeft.kind === "unlimited"
                        ? t("No limit")
                        : budgetLeft.kind === "unknown"
                          ? t("Unknown")
                          : displayMoney(budgetLeft.value)}
                    </td>
                    {showActions ? (
                      <td>
                        {record.is_active ? (
                          onPause ? (
                            <button
                              className="ops-button"
                              data-size="sm"
                              data-variant="secondary"
                              onClick={() => onPause(record)}
                              type="button"
                            >
                              {t("Pause")}
                            </button>
                          ) : null
                        ) : onResume ? (
                          <button
                            className="ops-button"
                            data-size="sm"
                            data-variant="secondary"
                            onClick={() => onResume(record)}
                            type="button"
                          >
                            {t("Resume")}
                          </button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </LoadState>
    </Section>
  );
}
